// D02 Unit 13 — Layer 3 Switching with SVIs (LAB: BUILD → BREAK → FIX → VERIFY)
import { note, readConfirm } from '../../ui/widgets.js';
import { mls, mlsLayout } from './topologies.js';
import { ran, pinged, reach, upIf, isTrunk } from './helpers.js';

export default {
  id: 'u13', num: '13', kind: 'LAB', title: 'Layer 3 Switching with SVIs', type: 'Configuration Lab', implemented: true,
  objective: 'Route between VLANs on a multilayer switch with SVIs, understand SVI autostate, and verify with the switch routing table.',
  network: () => mls({ routed: false }),
  layout: mlsLayout, linkLights: true,
  stages: [
    {
      id: 'learn', phase: 'LEARN', title: 'Routing inside the switch', workspace: false,
      content: `<p>A <b>multilayer switch</b> (here a Catalyst 3560) can route between VLANs in hardware. Each VLAN gets a <b>switch virtual interface</b> (SVI) that acts as the gateway.</p>
<pre>ip routing
interface vlan 10
 ip address 10.10.10.1 255.255.255.0
 no shutdown</pre>
<ul><li><code>ip routing</code> turns the switch into a router. Without it the SVIs are only management addresses.</li>
<li><b>Autostate</b>: an SVI is up/up only while at least one port carrying that VLAN is up. No port in VLAN 20 → Vlan20 goes down.</li>
<li>On a 3560, trunks need <code>switchport trunk encapsulation dot1q</code> before <code>switchport mode trunk</code> (already done on Gi0/1).</li></ul>`,
      widgets: [readConfirm({ id: 'd2u13-read' })],
    },
    {
      id: 'build', phase: 'BUILD → VERIFY', title: 'Turn MLS1 into the gateway', devices: ['MLS1', 'PC1'],
      content: '<table><tr><th>SVI</th><th>Address</th></tr><tr><td>Vlan10</td><td>10.10.10.1/24</td></tr><tr><td>Vlan20</td><td>10.10.20.1/24</td></tr><tr><td>Vlan99</td><td>10.10.99.1/24</td></tr></table>',
      evidence: [
        { id: 'route', text: 'MLS1: routing table shows connected VLAN subnets', match: (ev) => ran('MLS1', 'show ip route')(ev) && (ev.output.match(/^C\s+10\.10\.\d+\.0\/24 is directly connected, Vlan\d+/gm) || []).length >= 3 },
        { id: 'ping', text: 'PC1 (VLAN 10) reaches PC2 (VLAN 20)', match: pinged('PC1', '10.10.20.11', { success: true }) },
        { id: 'trace', text: 'PC1: tracert shows MLS1 as the first hop', match: (ev) => ran('PC1', 'tracert', /10\.10\.10\.1\b/)(ev) && ev.result?.success },
      ],
      tasks: [
        { id: 'ipr', text: 'ip routing enabled', check: (rt) => rt.net.devices.MLS1.ipRouting, fail: 'MLS1 is not routing.' },
        { id: 'svi', text: 'Vlan10, Vlan20, Vlan99 up/up with the planned addresses', check: (rt) => [10, 20, 99].every((v) => upIf(rt, 'MLS1', `Vlan${v}`) && rt.net.devices.MLS1.interfaces[`Vlan${v}`].ip?.ip === `10.10.${v}.1`), fail: 'An SVI is missing, down, or mis-addressed.' },
        { id: 'e2e', text: 'Inter-VLAN and management reachability', check: (rt) => reach(rt, 'PC1', '10.10.20.11') && reach(rt, 'PC2', '10.10.99.12'), fail: 'Inter-VLAN routing incomplete.' },
      ],
      hints: ['<code>ip routing</code> is a global command.', 'New SVIs start administratively down — remember <code>no shutdown</code>.'],
    },
    {
      id: 'break', phase: 'BREAK → TROUBLESHOOT → FIX', title: 'Support lost its gateway', devices: ['MLS1', 'SW2', 'PC2'],
      onEnter: (rt) => { rt.net.devices.MLS1.interfaces['GigabitEthernet0/1'].allowed = [10, 99]; },
      ticket: { id: '2013', priority: 2, from: 'Support team', subject: 'Cannot reach gateway 10.10.20.1', body: 'All Support PCs lost their gateway. Nobody touched the SVIs, according to the change log.' },
      evidence: [
        { id: 'svi', text: 'MLS1: SVI state checked', match: ran('MLS1', ['show ip interface brief', 'show interfaces <IF>', 'show interfaces']) },
        { id: 'trunk', text: 'MLS1: trunk towards SW2 checked', match: ran('MLS1', ['show interfaces trunk', 'show running-config interface <IF>', 'show interfaces <IF> switchport']) },
      ],
      tasks: [
        { id: 't', text: 'VLAN 20 carried to MLS1 again', check: (rt) => isTrunk(rt, 'MLS1', 'GigabitEthernet0/1', { allowed: [10, 20, 99] }), fail: 'VLAN 20 is still not allowed.' },
        { id: 'svi', text: 'Vlan20 up/up and PC2 reaches its gateway', check: (rt) => upIf(rt, 'MLS1', 'Vlan20') && reach(rt, 'PC2', '10.10.20.1'), fail: 'Vlan20 is still down.' },
      ],
      question: 'Vlan20 shows <code>down/down</code> though nobody shut it. Which rule explains that?',
      hints: ['Autostate: which ports on MLS1 carry VLAN 20?'],
    },
    {
      id: 'verify', phase: 'VERIFY → DOCUMENT', title: 'Verify', devices: ['MLS1', 'PC2'],
      evidence: [
        { id: 'svi', text: 'MLS1: Vlan20 up/up', match: ran('MLS1', 'show ip interface brief', /Vlan20\s+10\.10\.20\.1\s+YES manual up\s+up/) },
        { id: 'ping', text: 'PC2 reaches PC1', match: pinged('PC2', '10.10.10.11', { success: true }) },
      ],
      widgets: [note({ id: 'd2u13-doc', title: 'TICKET CLOSURE NOTE', prompt: 'Why the SVI went down, and the fix.', min: 40 })],
      verifyLabel: 'Verify & complete unit',
    },
  ],
};
